import { Injectable, OnDestroy } from '@angular/core';

/**
 * Same-session preview cache for chat images the user uploaded this session —
 * object URLs keyed by the `Document` uuid (which equals the staged file's
 * uuid; the composer registers it as `documentId: file.uuid`,
 * `chats.effects.ts`).
 *
 * **Why this exists (and why it's an optimization, not correctness):** when an
 * image finishes uploading, the composer drops the raw `File`
 * (`conversation-prompt-input.smart.component.ts` upload-done effect) and the
 * `Document` starts ingesting. There is a short `processing → ready` window
 * where the presigned repository object isn't servable yet — this cache lets
 * the overlay render the local blob across that gap instead of showing the
 * error state. The **durable** source is always the presigned URL: a rehydrated
 * chat (no local `File`) has no cache entry and correctly falls back to it.
 *
 * **Reuse (tech-debt note):** the deferred in-thread thumbnail will render
 * `<img [src]>` with the same `getPreviewUrl(uuid) ?? presigned` resolution, so
 * the chip→thumbnail swap is localized to the message bubble. Keep this store a
 * dependency-light leaf (only `@angular/core`) so it stays unit-testable — do
 * not import the chats `../shared` barrel (it transitively pulls the reducer +
 * environment stub and would make this untestable under jest).
 *
 * Page-scoped: provided in `ConversationPage` so the composer (captures) and the
 * overlay (reads) share one instance. Reset on chat switch and on destroy.
 */
@Injectable()
export class ChatImagePreviewStore implements OnDestroy {
  private readonly urls = new Map<string, string>();

  /**
   * Cache an object URL for `documentUuid`. **Idempotent** — safe to call on
   * every upload-progress tick; an already-cached document is left untouched
   * (so the URL is created once, the moment the `File` first appears).
   */
  public capture(documentUuid: string, file: File): void {
    if (this.urls.has(documentUuid)) {
      return;
    }
    this.urls.set(documentUuid, URL.createObjectURL(file));
  }

  /** The cached preview URL, or `null` if none (→ caller falls back to presigned). */
  public getPreviewUrl(documentUuid: string): string | null {
    return this.urls.get(documentUuid) ?? null;
  }

  /** Revoke + drop a single preview (e.g. the chip was removed before send). */
  public release(documentUuid: string): void {
    const url = this.urls.get(documentUuid);
    if (url) {
      URL.revokeObjectURL(url);
      this.urls.delete(documentUuid);
    }
  }

  /** Revoke everything (chat switch / teardown). */
  public reset(): void {
    for (const url of this.urls.values()) {
      URL.revokeObjectURL(url);
    }
    this.urls.clear();
  }

  public ngOnDestroy(): void {
    this.reset();
  }
}
